/**
 * useCreateRequestWithImport Hook
 * React Query hook for creating requests with file import
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { offlineRequestsKeys } from './useOfflineRequestsQuery';
import { requestsKeys } from './useRequestsQuery';
import { noteImportUpload } from '../../services/clientImportTiming';
import { transcriptionService } from '../../services/transcriptionService';

import type {
  CreateRequestWithImportData,
  CreateRequestWithImportResponse,
} from '../../types/transcription';

export const useCreateRequestWithImport = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateRequestWithImportResponse,
    Error,
    CreateRequestWithImportData,
    { startedAt: number }
  >({
    mutationFn: (data: CreateRequestWithImportData) =>
      transcriptionService.createRequestWithImport(data),

    // PERF-4: t0 ≈ the upload click (mutation start).
    onMutate: () => ({ startedAt: Date.now() }),

    onSuccess: (response, _variables, context) => {
      // PERF-4: link t0 to the new job so the COMPLETED transition can report.
      noteImportUpload(response.requestId, response.jobId, context.startedAt);

      // Invalidate all request list queries to show new PROCESSING request immediately
      queryClient.invalidateQueries({ queryKey: requestsKeys.all });
      queryClient.invalidateQueries({ queryKey: offlineRequestsKeys.lists() });

      // Optionally pre-populate the request cache
      queryClient.setQueryData(
        requestsKeys.detail(String(response.requestId)),
        {
          id: response.requestId,
          stage: 'PROCESSING',
          // ... other fields will be fetched when viewing the request
        },
      );

      console.log(
        `✅ Request ${response.requestId} created with job ${response.jobId}`,
      );
    },

    onError: error => {
      console.error('❌ Failed to create request with import:', error);
    },
  });
};
